import { useState } from "react";
import PropTypes from "prop-types";
import { Modal, Checkbox } from "@/config/antd";
import Avatar from "@/components/common/Avatar";
import Button from "@/components/common/Button";
import { CloseOutlined, SearchOutlined } from "@/config/antd";
import Input from "@/components/common/inputs/Input";
import Delete from "@/components/common/alerts/Delete";
import Success from "@/components/common/alerts/Success";
import { useLoader } from "@/hooks/LoaderProvider";

const MemberCard = ({ member, isSelected, onSelect }) => (
  <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-[#E1F0FF] mb-3">
    <div className="flex items-center gap-4">
      <Avatar src={member.avatar} size="large" shape="circle" />
      <div className="flex flex-col">
        <span className="text-regular font-semibold text-darkGrey">
          {member.name}
        </span>
        <span className="text-small text-gray-500">{member.email}</span>
      </div>
      <span className="text-primary text-small">{member.role}</span>
    </div>
    <Checkbox checked={isSelected} onChange={() => onSelect(member.id)} />
  </div>
);

const InvitedMembersDialog = ({ isVisible, onClose }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
  const { showLoader, hideLoader } = useLoader();

  const corporateMembers = [
    {
      id: 1,
      name: "Jide Ogunluyi",
      email: "jide.ogunluyi@yahoo.com",
      avatar: null,
      role: "Speaker",
    },
    {
      id: 2,
      name: "Mary Wilson",
      email: "mary.wilson@gmail.com",
      avatar: null,
      role: "Speaker",
    },
    {
      id: 3,
      name: "David Chen",
      email: "david.chen@outlook.com",
      avatar: null,
      role: "Coordinator",
    },
    {
      id: 4,
      name: "Lisa Anderson",
      email: "lisa.anderson@yahoo.com",
      avatar: null,
      role: "Speaker",
    },
  ];

  const handleSelectMember = (memberId) => {
    setSelectedMembers((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleProceed = () => {
    if (selectedMembers.length > 0) {
      setIsDeleteModalVisible(true);
    }
  };

  const getSelectedMembersDetails = () => {
    return selectedMembers.map((id) =>
      corporateMembers.find((member) => member.id === id)
    );
  };

  const createConfirmationMessage = () => {
    const selectedMembersDetails = getSelectedMembersDetails();
    const membersList = selectedMembersDetails
      .map(
        (member, index) =>
          `<li style="margin-bottom: 8px; font-weight: 600; color: var(--veegil-color-dark-gray)">
           ${index + 1}. ${member.name}
         </li>`
      )
      .join("");

    return `
      <div style="text-align: center;">
        <span style="color: black;">
          Are you sure you want to select the following users?
        </span>
        <br/><br/>
        <ul style="list-style: none; text-align: center;">
          ${membersList}
        </ul>
      </div>
    `;
  };

  const createSuccessMessage = () => {
    const selectedMembersDetails = getSelectedMembersDetails();
    const membersList = selectedMembersDetails
      .map(
        (member, index) =>
          `<li style="margin-bottom: 8px; font-weight: 600; color: var(--veegil-color-dark-gray)">
           ${index + 1}. ${member.name}
         </li>`
      )
      .join("");

    return `
      <div style="text-align: center;">
        <span style="color: black;">
          You have successfully invited:
        </span>
        <br/><br/>
        <ul style="list-style: none; text-align: center;">
          ${membersList}
        </ul>
      </div>
    `;
  };

  const handleSelectionProceed = () => {
    setIsDeleteModalVisible(false);
    onClose();
    showLoader();
    setTimeout(() => {
      hideLoader();
      setIsSuccessModalVisible(true);
    }, 2000);
  };

  return (
    <>
      <Modal
        open={isVisible}
        onCancel={onClose}
        footer={null}
        centered
        width={600}
        closeIcon={null}
        styles={{
          body: { padding: 0 },
          content: {
            padding: 0,
            borderRadius: "16px",
            overflow: "hidden",
          },
          mask: { backgroundColor: "rgba(0, 0, 0, 0.75)" },
        }}>
        <div className="flex flex-col bg-white">
          {/* Header */}
          <div className="p-4 px-6 flex justify-between items-center">
            <h2 className="text-bold-x text-primary">Invited Members</h2>
            <CloseOutlined
              onClick={onClose}
              className="cursor-pointer"
              style={{
                color: "var(--veegil-color-primary)",
                backgroundColor: "var(--veegil-color-lighter-blue)",
                borderRadius: "50%",
                padding: "4px",
              }}
            />
          </div>

          {/* Content Area */}
          <div className="flex flex-col px-6 pb-6 space-y-6">
            {/* Search Input */}
            <Input
              placeholder="Search User"
              icon={<SearchOutlined />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            {/* Corporate Members Section */}
            <div>
              <h3 className="text-darkGrey text-bold font-semibold mb-4">
                CORPORATE MEMBERS
              </h3>
              <div className="space-y-2">
                {corporateMembers.map((member) => (
                  <MemberCard
                    key={member.id}
                    member={member}
                    isSelected={selectedMembers.includes(member.id)}
                    onSelect={handleSelectMember}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="w-full bg-lighterBlue p-4 flex gap-3">
            <Button
              text="CANCEL"
              color="secondary"
              className="flex-1 border border-primary"
              customBgColor="white"
              customTextColor="primary"
              onClick={onClose}
            />
            <Button
              text="PROCEED"
              color="primary"
              className="flex-1 bg-primary text-white"
              customBgColor="primary"
              customTextColor="white"
              onClick={handleProceed}
            />
          </div>
        </div>
      </Modal>

      <Delete
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onProceed={handleSelectionProceed}
        header="Proceed with Selection?"
        paragraph={createConfirmationMessage()}
        proceedButtonText="PROCEED"
        cancelButtonText="CANCEL"
      />
      <Success
        isVisible={isSuccessModalVisible}
        onClose={() => setIsSuccessModalVisible(false)}
        header="New Users Invited!"
        paragraph={createSuccessMessage()}
        buttonText="CLOSE"
        onButtonClick={() => setIsSuccessModalVisible(false)}
      />
    </>
  );
};

MemberCard.propTypes = {
  member: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    email: PropTypes.string,
    avatar: PropTypes.string,
    role: PropTypes.string,
  }),
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func,
};

InvitedMembersDialog.propTypes = {
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
};

export default InvitedMembersDialog;
